/**
 * @file InventoryPriceChanges.js
 * @version 1.6.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-04-27
 * @modified 2026-10-12
 * @description Stage cost / sales-price updates against existing inventory
 *              items locally, then bulk-sync them to Xero when ready. Changes
 *              can also be read in bulk from a price list (screenshot, PDF,
 *              spreadsheet or pasted text) via PriceChangeImportModal.
 *
 *              DATES (2026-10-07): each change carries the day it takes effect
 *              (today by default) and may be set to auto-post — the worker posts
 *              it at 05:30 London that morning. A change not on auto-post that
 *              is still queued on or after its date shows its date in bold red
 *              ("Due on … — not posted yet"); a future one is muted, "Scheduled".
 *              Posting by hand ahead of the date asks first. Until the
 *              migration has run the server says scheduling: false and none of
 *              the date controls show. Rules: utils/priceChangeDates.js.
 *
 *              ORDERS ON ANOTHER COST (2026-10-12, Cam): after a post, the
 *              orders and POs delivered on or after the change's effective
 *              date (not yet on a customer invoice) that are on another cost
 *              are offered in PriceSyncUpdatesModal — nothing changes unless
 *              the person ticks and updates. A change the 05:30 job posted is
 *              marked "Orders to review"; "Review orders" on any posted cost
 *              change, and the header's "Orders to review", open the same
 *              window. Saving or scheduling a cost dated before today says
 *              what will be asked on posting.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { API_URL } from '../config';
import PriceSyncUpdatesModal from './PriceSyncUpdatesModal';
import PriceChangeImportModal from './PriceChangeImportModal';
import './SearchLayout.css';
import './InventoryPriceChanges.css';
import Input from './Input';
import Dropdown from './Dropdown';
import SearchableDropdown from './SearchableDropdown';
import LoadingSpinner from './LoadingSpinner';
import LoadingBar from './LoadingBar';
import AlertDialog from './AlertDialog';
import ConfirmDialog from './ConfirmDialog';
import AuditHistoryModal from './AuditHistoryModal';
import PaginationControls from './PaginationControls';
import { useSlideOutButton } from './SlideOutButton';
import { useAccess } from '../hooks/useAccess';
import { formatPrice } from '../utils/currencyFormatter';
import { formatDecimalInput } from '../utils/numberFormatters';
import usePersistedState from '../hooks/usePersistedState';
import useLatestRequest from '../hooks/useLatestRequest';
import SearchHelp from './SearchHelp';
import { activeHeaderFilters, clearFiltersClass } from '../utils/activeFilters';
import DateInput from './DateInput';
import {
  londonToday, dateState, dateTitle, formatEffectiveDate, isFutureDated, futurePostMessage,
  postsTomorrow, POSTS_TOMORROW_TEXT, isBackdatedCost, backdatedNotice, reviewBlock
} from '../utils/priceChangeDates';

// effective_date '' means "today" — filled in from the server's date on save.
const EMPTY_FORM = { id: null, inventory_item_id: '', cost: '', sales_price: '', effective_date: '', auto_post: false };

/*
 * What Clear Filters resets to — and so what "no filters applied" means for
 * the button's highlight (utils/activeFilters.js).
 */
const CLEARED_FILTERS = { search: '', showSynced: false, limit: '50' };

function InventoryPriceChanges() {
  const [items, setItems] = useState([]); // available inventory items for dropdown
  const [rows, setRows] = useState([]); // queued price changes
  const [total, setTotal] = useState(0); // total matching rows across all pages
  const [page, setPage] = usePersistedState('priceChanges.page', 1);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = usePersistedState('priceChanges.filters', CLEARED_FILTERS);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [syncProgress, setSyncProgress] = useState(0);
  // The orders-on-another-cost window: { changeIds } and the sentence above it.
  const [reprice, setReprice] = useState(null);
  // Posted changes nobody has answered the offer for (the 05:30 job's, or closed with ×).
  const [pendingReview, setPendingReview] = useState([]);
  const { can } = useAccess();
  const seeCosts = can('costs.view');
  const [syncMessage, setSyncMessage] = useState('');
  const [alertDialog, setAlertDialog] = useState({ isOpen: false, message: '', type: 'info' });
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, message: '', onConfirm: null });
  const [historyRow, setHistoryRow] = useState(null);
  // Price list import: offered only when the server has a key to read with.
  const [importAvailable, setImportAvailable] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  // Dates and auto-post: shown once the server has the columns (its list
  // response says so). `today` is the server's London date.
  const [scheduling, setScheduling] = useState(false);
  const [today, setToday] = useState(() => londonToday());
  const [bulkDate, setBulkDate] = useState('');

  // Any header filter lights the Clear Filters button.
  const headerFilters = activeHeaderFilters(filters, CLEARED_FILTERS, {
    labels: { showSynced: 'Show synced' }
  });
  const filtersApplied = headerFilters.count > 0;

  useSlideOutButton([rows.length, selectedIds.size, form.id, importAvailable, scheduling, pendingReview.length]);

  const loadPendingReview = async () => {
    if (!seeCosts) { setPendingReview([]); return; }
    try {
      const res = await fetch(`${API_URL}/api/price-sync-updates/changes`, { credentials: 'include' });
      const data = await res.json();
      setPendingReview(res.ok && data.success ? (data.pending || []) : []);
    } catch (e) {
      setPendingReview([]);
    }
  };

  // After a post: ask about the orders it leaves on another cost. `answer` is the
  // server's { lines, change_ids }, or null when it could not be worked out —
  // then the window works it out itself.
  const offerRepricing = (answer, postedIds, what) => {
    loadPendingReview();
    if (answer && !answer.lines) return;
    const ids = answer?.change_ids?.length ? answer.change_ids : postedIds;
    if (!ids.length) return;
    const n = answer?.lines;
    setReprice({
      changeIds: ids,
      intro: n
        ? `${what} posted. ${n} order and PO line${n === 1 ? '' : 's'} delivered on or after the effective date ${n === 1 ? 'is' : 'are'} still on another cost — tick the ones to update.`
        : `${what} posted. Check the orders and POs delivered on or after the effective date.`
    });
  };

  // --- loaders --------------------------------------------------------------
  const loadItems = async () => {
    try {
      const res = await fetch(
        `${API_URL}/api/inventory/search?limit=10000&activeOnly=true&isSold=true`,
        { credentials: 'include' }
      );
      const data = await res.json();
      setItems(data.items || data.data || []);
    } catch (e) {
      console.error('Failed to load inventory items:', e);
    }
  };

  // Responses come back out of order; only the newest may write the rows.
  const latest = useLatestRequest();

  const loadRows = async () => {
    const seq = latest.begin();
    setLoading(true);
    try {
      const limitNum = parseInt(filters.limit, 10) || 50;
      const params = new URLSearchParams({
        showSynced: filters.showSynced ? 'true' : 'false',
        limit: String(limitNum),
        offset: String((page - 1) * limitNum)
      });
      if (filters.search) params.set('search', filters.search);
      const res = await fetch(`${API_URL}/api/price-changes?${params}`, { credentials: 'include' });
      const data = await res.json();
      if (!latest.isLatest(seq)) return;   // superseded by a later search
      setRows(data.rows || []);
      setTotal(typeof data.total === 'number' ? data.total : (data.rows?.length || 0));
      setScheduling(!!data.scheduling);
      if (data.today) setToday(data.today);
    } catch (e) {
      if (!latest.isLatest(seq)) return;
      console.error('Failed to load price changes:', e);
      setAlertDialog({ isOpen: true, message: 'Failed to load price changes', type: 'error' });
    } finally {
      if (latest.isLatest(seq)) setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
    loadPendingReview();
    fetch(`${API_URL}/api/price-changes/extract/status`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setImportAvailable(!!d?.available))
      .catch(() => setImportAvailable(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter changes always send the user back to page 1 — otherwise we'd
  // request offset N on a query that may now return < N rows. Not on the
  // first run: that would throw away the page restored from the nav store.
  const firstFilterRun = useRef(true);
  useEffect(() => {
    if (firstFilterRun.current) { firstFilterRun.current = false; return; }
    setPage(1);
  }, [filters.search, filters.showSynced, filters.limit, setPage]);

  useEffect(() => {
    const t = setTimeout(loadRows, filters.search ? 300 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search, filters.showSynced, filters.limit, page]);

  // --- helpers --------------------------------------------------------------
  const itemOptions = useMemo(() => items.map(it => ({
    value: it.id.toString(),
    badge: it.item_code || '',
    label: it.item_name,
    searchText: `${it.item_code || ''} ${it.item_name || ''}`
  })), [items]);

  const selectedItem = useMemo(() => {
    if (!form.inventory_item_id) return null;
    return items.find(it => it.id.toString() === form.inventory_item_id) || null;
  }, [form.inventory_item_id, items]);

  const handleSelectItem = (value) => {
    const it = items.find(x => x.id.toString() === value);
    setForm(prev => ({
      ...prev,
      inventory_item_id: value,
      // 2dp minimum, up to 4 where the figure has them; blank stays ''.
      // handleSave parses both before sending.
      cost: formatDecimalInput(it?.purchase_unit_price),
      sales_price: formatDecimalInput(it?.sales_unit_price)
    }));
  };

  const resetForm = () => setForm(EMPTY_FORM);

  // --- save / edit ----------------------------------------------------------
  const handleSave = async () => {
    if (!form.inventory_item_id) {
      setAlertDialog({ isOpen: true, message: 'Select a product first.', type: 'warning' });
      return;
    }
    if ((form.cost === '' || form.cost == null) && (form.sales_price === '' || form.sales_price == null)) {
      setAlertDialog({ isOpen: true, message: 'Enter at least one of cost or sales price.', type: 'warning' });
      return;
    }
    setSaving(true);
    try {
      const url = form.id
        ? `${API_URL}/api/price-changes/${form.id}`
        : `${API_URL}/api/price-changes`;
      const method = form.id ? 'PUT' : 'POST';
      const body = {
        inventory_item_id: parseInt(form.inventory_item_id, 10),
        cost: form.cost === '' ? null : parseFloat(form.cost),
        sales_price: form.sales_price === '' ? null : parseFloat(form.sales_price)
      };
      if (scheduling) {
        body.effective_date = form.effective_date || today;
        body.auto_post = !!form.auto_post;
      }
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (!res.ok || data.error) throw new Error(data.error || 'Save failed');
      resetForm();
      await loadRows();
      // Dated before today: on posting, orders delivered since are offered.
      const backdated = scheduling && data.row && isBackdatedCost(data.row, today)
        ? backdatedNotice([data.row.effective_date])
        : null;
      // Due already, auto-post on, and this morning's run has been: it would
      // sit until tomorrow. Say so while posting it now is still one click.
      if (scheduling && data.row && postsTomorrow(data.row, { today })) {
        const saved = data.row;
        setConfirmDialog({
          isOpen: true,
          message: `${backdated ? `${backdated}\n\n` : ''}${POSTS_TOMORROW_TEXT} Post ${saved.item_code || 'it'} to Xero now instead?`,
          confirmText: 'Post now',
          cancelText: 'Leave for tomorrow',
          onConfirm: () => {
            setConfirmDialog(prev => ({ ...prev, isOpen: false }));
            handleSync(saved);
          }
        });
      } else if (backdated) {
        setAlertDialog({ isOpen: true, message: backdated, type: 'info' });
      }
    } catch (e) {
      setAlertDialog({ isOpen: true, message: e.message, type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (row) => {
    setForm({
      id: row.id,
      inventory_item_id: row.inventory_item_id.toString(),
      cost: formatDecimalInput(row.cost),
      sales_price: formatDecimalInput(row.sales_price),
      effective_date: row.effective_date ? String(row.effective_date).slice(0, 10) : '',
      auto_post: !!row.auto_post
    });
    // Scroll to top so the form is visible
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // --- delete ---------------------------------------------------------------
  const handleDelete = (row) => {
    setConfirmDialog({
      isOpen: true,
      message: `Delete the queued price change for ${row.item_code} — ${row.item_name}?`,
      confirmText: 'Delete',
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
        try {
          const res = await fetch(`${API_URL}/api/price-changes/${row.id}`, {
            method: 'DELETE',
            credentials: 'include'
          });
          if (!res.ok) throw new Error('Delete failed');
          loadRows();
        } catch (e) {
          setAlertDialog({ isOpen: true, message: e.message, type: 'error' });
        }
      }
    });
  };

  const handleBulkDelete = () => {
    if (selectedIds.size === 0) return;
    setConfirmDialog({
      isOpen: true,
      message: `Delete ${selectedIds.size} queued price change${selectedIds.size !== 1 ? 's' : ''}?`,
      confirmText: 'Delete',
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
        try {
          const res = await fetch(`${API_URL}/api/price-changes/bulk-delete`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ ids: Array.from(selectedIds) })
          });
          if (!res.ok) throw new Error('Bulk delete failed');
          setSelectedIds(new Set());
          loadRows();
        } catch (e) {
          setAlertDialog({ isOpen: true, message: e.message, type: 'error' });
        }
      }
    });
  };

  // --- sync -----------------------------------------------------------------
  // Posting ahead of a change's date is allowed, but asked about first. The
  // server asks too (409 FUTURE_DATED) in case its date and ours disagree.
  const askThenPost = (message, post) => {
    setConfirmDialog({
      isOpen: true,
      message,
      confirmText: 'Post now',
      onConfirm: () => {
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
        post();
      }
    });
  };

  const handleSync = async (row, confirmed = false) => {
    if (!confirmed && scheduling && isFutureDated(row, today)) {
      askThenPost(futurePostMessage([row]), () => handleSync(row, true));
      return;
    }
    setSyncProgress(10);
    setSyncMessage(`Syncing ${row.item_code}...`);
    try {
      const res = await fetch(`${API_URL}/api/price-changes/${row.id}/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(confirmed ? { confirm_future: true } : {})
      });
      const data = await res.json();
      if (res.status === 409 && data.code === 'FUTURE_DATED' && !confirmed) {
        setSyncProgress(0);
        setSyncMessage('');
        askThenPost(data.error, () => handleSync(row, true));
        return;
      }
      if (!res.ok || data.error) throw new Error(data.error || 'Sync failed');
      setSyncProgress(100);
      setSyncMessage('Synced');
      setTimeout(() => { setSyncProgress(0); setSyncMessage(''); }, 800);
      loadRows();
      // Orders and POs delivered since its effective date, on another cost:
      // ask now, while the person who moved the price is here.
      if (row.cost !== null && row.cost !== undefined && row.cost !== '') {
        offerRepricing(data.reprice, [row.id], row.item_code || 'The change');
      }
    } catch (e) {
      setSyncProgress(0);
      setSyncMessage('');
      setAlertDialog({ isOpen: true, message: e.message, type: 'error' });
      loadRows();   // the row now carries the error
    }
  };

  const handleBulkSync = async (confirmed = false) => {
    if (selectedIds.size === 0) return;
    if (!confirmed && scheduling) {
      const ahead = rows.filter(r => selectedIds.has(r.id) && isFutureDated(r, today));
      if (ahead.length) {
        askThenPost(futurePostMessage(ahead), () => handleBulkSync(true));
        return;
      }
    }
    setSyncProgress(10);
    setSyncMessage(`Syncing ${selectedIds.size} item${selectedIds.size !== 1 ? 's' : ''}...`);
    try {
      const res = await fetch(`${API_URL}/api/price-changes/bulk-sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ids: Array.from(selectedIds), ...(confirmed ? { confirm_future: true } : {}) })
      });
      const data = await res.json();
      if (res.status === 409 && data.code === 'FUTURE_DATED' && !confirmed) {
        setSyncProgress(0);
        setSyncMessage('');
        askThenPost(data.error, () => handleBulkSync(true));
        return;
      }
      if (!res.ok || data.error) throw new Error(data.error || 'Bulk sync failed');
      setSyncProgress(100);
      const okCount = data.results?.synced?.length || 0;
      const failCount = data.results?.failed?.length || 0;
      const skipCount = data.results?.skipped?.length || 0;
      setSyncMessage(`Synced ${okCount}${failCount ? `, ${failCount} failed` : ''}${skipCount ? `, ${skipCount} already posted` : ''}`);
      if (okCount > 0) {
        offerRepricing(data.results?.reprice ?? null,
          (data.results?.synced || []).map(r => r.id),
          `${okCount} change${okCount === 1 ? '' : 's'}`);
      }
      if (failCount > 0) {
        const errs = data.results.failed.map(f => `#${f.id}: ${f.error}`).join('\n');
        setAlertDialog({ isOpen: true, message: `Some rows failed:\n${errs}`, type: 'warning' });
      }
      setTimeout(() => { setSyncProgress(0); setSyncMessage(''); }, 1200);
      setSelectedIds(new Set());
      loadRows();
    } catch (e) {
      setSyncProgress(0);
      setSyncMessage('');
      setAlertDialog({ isOpen: true, message: e.message, type: 'error' });
    }
  };

  // --- schedule (bulk) -------------------------------------------------------
  // Set the date and/or auto-post on the selected changes. Posted ones are
  // left alone by the server.
  const handleBulkSchedule = async (patch) => {
    if (selectedIds.size === 0) return;
    try {
      const res = await fetch(`${API_URL}/api/price-changes/bulk-schedule`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ids: Array.from(selectedIds), ...patch })
      });
      const data = await res.json();
      if (!res.ok || data.error) throw new Error(data.error || 'Could not update the selected changes');
      const notes = [];
      if (data.skipped) {
        notes.push(`${data.updated} change${data.updated === 1 ? '' : 's'} updated; ${data.skipped} already posted ${data.skipped === 1 ? 'was' : 'were'} left as ${data.skipped === 1 ? 'it was' : 'they were'}.`);
      }
      // What the rows will look like now, to spot any that will wait a day.
      const waiting = rows.filter(r => selectedIds.has(r.id)).filter(r => postsTomorrow({
        ...r,
        ...(patch.auto_post !== undefined ? { auto_post: patch.auto_post } : {}),
        effective_date: patch.effective_date || r.effective_date || (patch.auto_post ? today : null)
      }, { today }));
      if (waiting.length) {
        notes.push(`${waiting.length === 1 ? 'One of these is' : `${waiting.length} of these are`} due already. ${POSTS_TOMORROW_TEXT} Use Sync to Xero to post now instead.`);
      }
      if (patch.effective_date) {
        const back = rows.filter(r => selectedIds.has(r.id))
          .filter(r => isBackdatedCost({ ...r, effective_date: patch.effective_date }, today));
        const note = back.length ? backdatedNotice([patch.effective_date]) : null;
        if (note) notes.push(note);
      }
      if (notes.length) setAlertDialog({ isOpen: true, type: 'info', message: notes.join('\n\n') });
      loadRows();
    } catch (e) {
      setAlertDialog({ isOpen: true, message: e.message, type: 'error' });
    }
  };

  // --- selection ------------------------------------------------------------
  const toggleSelect = (id) => setSelectedIds(prev => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const toggleSelectAll = () => {
    if (selectedIds.size === rows.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(rows.map(r => r.id)));
  };

  // --- render ---------------------------------------------------------------
  const renderPriceCell = (current, next) => {
    const c = parseFloat(current);
    const n = parseFloat(next);
    if (next == null || isNaN(n)) return <span style={{ color: 'var(--text-muted)' }}>—</span>;
    const direction = !isNaN(c) ? Math.sign(n - c) : 0;
    const arrow = direction > 0 ? '▲' : direction < 0 ? '▼' : '';
    const color = direction > 0 ? 'var(--color-success)' : direction < 0 ? 'var(--color-error)' : 'var(--text-secondary)';
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
        <span style={{ fontWeight: 600, color }}>
          {formatPrice(n)} {arrow && <span style={{ fontSize: '0.8em' }}>{arrow}</span>}
        </span>
        {!isNaN(c) && (
          <span style={{ fontSize: 'var(--font-xs)', color: 'var(--text-muted)' }}>
            from {formatPrice(c)}
          </span>
        )}
      </div>
    );
  };

  const renderDateCell = (row) => {
    const st = dateState(row, today);
    return (
      <div className={`pc-date ${st}`} title={dateTitle(row, today)}>
        <span className="pc-date-value">{formatEffectiveDate(row.effective_date)}</span>
        {st === 'scheduled' && <span className="pc-date-note">Scheduled</span>}
        {row.auto_post && !row.is_synced && (
          <span className="pc-auto-post">
            <span className="material-symbols-rounded">schedule_send</span>Auto-post
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="search-inventory inventory-price-changes">
      <div className="search-header">
        <div className="workflow-info" style={{ gridColumn: '1' }}>
          <span className="info">
            {scheduling
              ? 'Stage cost / sales-price updates with the date they take effect, then post them to Xero — by hand, or automatically at 05:30 on the day.'
              : 'Stage cost / sales-price updates locally, then bulk-sync them to Xero when the new prices are due to go live.'}
          </span>
        </div>
        <div className='search-header-inner' style={{ gridColumn: '2' }}>
          <div className="buttons slideout">
            {importAvailable && (
              <div>
                <button
                  className="btn-slideout"
                  data-write-action
                  onClick={() => setImportOpen(true)}
                  title="Read prices from a screenshot, PDF, spreadsheet or pasted text"
                >
                  <div className="icon-wrapper">
                    <span className="material-symbols-rounded">document_scanner</span>
                  </div>
                  <span className="btn-label">Import Price List</span>
                </button>
              </div>
            )}
            <div>
              <button
                className="btn-slideout"
                onClick={() => setReprice({
                  changeIds: pendingReview,
                  intro: 'Posted changes whose orders and POs nobody has looked at yet — posted automatically at 05:30, or the question was closed.'
                })}
                disabled={!seeCosts || !pendingReview.length}
                title={!seeCosts
                  ? 'Needs permission to see costs'
                  : pendingReview.length
                    ? 'Orders and POs still on another cost after a change was posted'
                    : 'No posted change has orders waiting to be reviewed'}
              >
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">price_change</span>
                </div>
                <span className="btn-label">Orders to review{pendingReview.length ? ` (${pendingReview.length})` : ''}</span>
              </button>
            </div>
            <div>
              <button
                className={clearFiltersClass(filtersApplied)}
                onClick={() => { setFilters(prev => ({ ...CLEARED_FILTERS, limit: prev.limit || '50' })); setPage(1); }}
                title={filtersApplied ? 'Clear all filters' : 'No filters applied'}
              >
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">filter_alt_off</span>
                </div>
                <span className="btn-label">Clear Filters</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Inline form for adding / editing a price change */}
      <div className="price-change-form">
        <div className={`price-change-form-grid${scheduling ? ' with-date' : ''}`}>
          <div className="form-group" style={{ flex: 2, minWidth: 240 }}>
            <label>Product *</label>
            <SearchableDropdown
              value={form.inventory_item_id}
              onChange={handleSelectItem}
              options={itemOptions}
              placeholder="Search by code or name..."
              searchPlaceholder="Search..."
              disabled={!!form.id /* product is fixed when editing */}
            />
          </div>
          <div className="form-group">
            <label>Cost (£)</label>
            <Input
              type="number"
              step="0.01"
              value={form.cost}
              onChange={(e) => setForm(prev => ({ ...prev, cost: e.target.value }))}
              placeholder={selectedItem?.purchase_unit_price != null ? `Current ${formatPrice(selectedItem.purchase_unit_price)}` : '0.00'}
            />
          </div>
          <div className="form-group">
            <label>Sales Price (£)</label>
            <Input
              type="number"
              step="0.01"
              value={form.sales_price}
              onChange={(e) => setForm(prev => ({ ...prev, sales_price: e.target.value }))}
              placeholder={selectedItem?.sales_unit_price != null ? `Current ${formatPrice(selectedItem.sales_unit_price)}` : '0.00'}
            />
          </div>
          {scheduling && (
            <div className="form-group pc-form-date">
              <label>Effective Date</label>
              <DateInput
                value={form.effective_date || today}
                onChange={(e) => setForm(prev => ({ ...prev, effective_date: e.target.value }))}
              />
            </div>
          )}
          {scheduling && (
            <div className="form-group pc-form-auto">
              <label htmlFor="pc-auto-post" title="Post this change to Xero automatically at 05:30 on its date">Auto-post</label>
              <div className="checkbox-container">
                <div className="check light">
                  <input
                    id="pc-auto-post"
                    type="checkbox"
                    checked={!!form.auto_post}
                    onChange={(e) => setForm(prev => ({ ...prev, auto_post: e.target.checked }))}
                  />
                  <label htmlFor="pc-auto-post"></label>
                </div>
              </div>
            </div>
          )}
          <div className="form-actions">
            <button className="btn-slideout" onClick={handleSave} disabled={saving}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">save</span></div>
              <span className="btn-label">{form.id ? 'Update' : 'Queue'}</span>
            </button>
            {form.id && (
              <button className="btn-round btn-danger" onClick={resetForm} disabled={saving}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
                <span className="btn-label">Cancel Edit</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="search-filters">
        <div className="filter-row" style={{ gridTemplateColumns: '1fr 130px 130px' }}>
          <div className="filter-group">
            <label>
              Search
              <SearchHelp fields={['item code', 'item name']} />
            </label>
            <Input
              type="text"
              placeholder="Item code or name..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              onEnter={() => loadRows()}
              icon="search"
            />
          </div>
          <div className="filter-group">
            <label>Per Page</label>
            <Dropdown
              value={filters.limit}
              onChange={(value) => setFilters(prev => ({ ...prev, limit: value }))}
              options={[
                { value: '20', label: '20' },
                { value: '50', label: '50' },
                { value: '100', label: '100' },
                { value: '200', label: '200' }
              ]}
              placeholder="50"
            />
          </div>
          <div className="filter-group">
            <label>Show Synced</label>
            <div className="checkbox-container">
              <div className="check light">
                <input
                  id="show-synced"
                  type="checkbox"
                  checked={filters.showSynced}
                  onChange={(e) => setFilters(prev => ({ ...prev, showSynced: e.target.checked }))}
                />
                <label htmlFor="show-synced"></label>
              </div>
            </div>
          </div>
        </div>
      </div>

      {syncProgress > 0 && (
        <div className="loading-bar-wrapper">
          <LoadingBar progress={syncProgress} message={syncMessage} />
        </div>
      )}

      {selectedIds.size > 0 && (
        <div className="bulk-actions-bar">
          <div className="bulk-actions-info">
            <span className="bulk-actions-title">Bulk Actions:</span>
            <span>{selectedIds.size} selected</span>
          </div>
          <div className="bulk-actions-buttons slideout">
            {scheduling && (
              <div className="pc-bulk-date">
                <DateInput
                  value={bulkDate || today}
                  onChange={(e) => setBulkDate(e.target.value)}
                />
              </div>
            )}
            {scheduling && (
              <div>
                <button
                  className="btn-slideout"
                  onClick={() => handleBulkSchedule({ effective_date: bulkDate || today })}
                  title="Give the selected changes this effective date"
                >
                  <div className="icon-wrapper"><span className="material-symbols-rounded">event</span></div>
                  <span className="btn-label">Set Date</span>
                </button>
              </div>
            )}
            {scheduling && (
              <div>
                <button
                  className="btn-slideout"
                  onClick={() => handleBulkSchedule({ auto_post: true })}
                  title="Post the selected changes automatically at 05:30 on their dates"
                >
                  <div className="icon-wrapper"><span className="material-symbols-rounded">schedule_send</span></div>
                  <span className="btn-label">Auto-post On</span>
                </button>
              </div>
            )}
            {scheduling && (
              <div>
                <button
                  className="btn-slideout"
                  onClick={() => handleBulkSchedule({ auto_post: false })}
                  title="Leave the selected changes queued until someone posts them"
                >
                  <div className="icon-wrapper"><span className="material-symbols-rounded">cancel_schedule_send</span></div>
                  <span className="btn-label">Auto-post Off</span>
                </button>
              </div>
            )}
            <div>
              <button className="btn-slideout" onClick={() => handleBulkSync()}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">cloud_upload</span></div>
                <span className="btn-label">Sync to Xero</span>
              </button>
            </div>
            <div>
              <button className="btn-slideout btn-danger" onClick={handleBulkDelete}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">delete</span></div>
                <span className="btn-label">Delete</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="table-toolbar">
        {(() => {
          const limitNum = parseInt(filters.limit, 10) || 50;
          const totalPages = Math.max(1, Math.ceil(total / limitNum));
          return (
            <PaginationControls
              pagination={{ page, limit: limitNum, total, totalPages }}
              onPageChange={setPage}
            />
          );
        })()}
      </div>

      <div className="table-container">
        {loading ? (
          <LoadingSpinner />
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th className="checkbox-col">
                  <div className="tick-check-label" style={{ justifyContent: 'center', gap: '0' }}>
                    <input
                      id="select-all-price-changes"
                      type="checkbox"
                      className="tick-check-input"
                      checked={rows.length > 0 && selectedIds.size === rows.length}
                      onChange={toggleSelectAll}
                    />
                    <label htmlFor="select-all-price-changes" className="tick-check">
                      <svg width="16px" height="16px" viewBox="0 0 18 18">
                        <path d="M1,9 L1,3.5 C1,2 2,1 3.5,1 L14.5,1 C16,1 17,2 17,3.5 L17,14.5 C17,16 16,17 14.5,17 L3.5,17 C2,17 1,16 1,14.5 L1,9 Z"></path>
                        <polyline points="1 9 7 14 15 4"></polyline>
                      </svg>
                    </label>
                  </div>
                </th>
                <th>Item Code</th>
                <th>Item Name</th>
                <th className="text-right">New Cost</th>
                <th className="text-right">New Sales Price</th>
                {scheduling && <th>Effective</th>}
                <th>Status</th>
                <th className="actions-col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={scheduling ? 8 : 7} className="text-center" style={{ color: 'var(--text-muted)', padding: 'var(--spacing-xl)' }}>
                  No queued price changes.
                </td></tr>
              ) : rows.map(row => (
                <tr key={row.id} className={row.is_synced ? 'synced' : ''}>
                  <td className="checkbox-col">
                    <div className="tick-check-label" style={{ justifyContent: 'center', gap: '0' }}>
                      <input
                        id={`select-pc-${row.id}`}
                        type="checkbox"
                        className="tick-check-input"
                        checked={selectedIds.has(row.id)}
                        onChange={() => toggleSelect(row.id)}
                      />
                      <label htmlFor={`select-pc-${row.id}`} className="tick-check">
                        <svg width="16px" height="16px" viewBox="0 0 18 18">
                          <path d="M1,9 L1,3.5 C1,2 2,1 3.5,1 L14.5,1 C16,1 17,2 17,3.5 L17,14.5 C17,16 16,17 14.5,17 L3.5,17 C2,17 1,16 1,14.5 L1,9 Z"></path>
                          <polyline points="1 9 7 14 15 4"></polyline>
                        </svg>
                      </label>
                    </div>
                  </td>
                  <td>{row.item_code}</td>
                  <td>{row.item_name}</td>
                  <td className="text-right">{renderPriceCell(row.current_cost, row.cost)}</td>
                  <td className="text-right">{renderPriceCell(row.current_sales_price, row.sales_price)}</td>
                  {scheduling && <td>{renderDateCell(row)}</td>}
                  <td>
                    <div className="pc-status">
                      {row.is_synced
                        ? (
                          <span className="synced-badge" title={row.posted_via === 'auto' ? 'Posted automatically on its date' : undefined}>
                            {row.posted_via === 'auto' ? 'AUTO-POSTED' : 'SYNCED'}
                          </span>
                        )
                        : <span className="pending-badge">PENDING</span>}
                      {!row.is_synced && row.post_error && (
                        <span className="pc-post-error material-symbols-rounded" title={`Last attempt to post failed: ${row.post_error}`}>error</span>
                      )}
                      {row.is_synced && row.reprice_review === 'pending' && (
                        <span
                          className="pc-review-badge"
                          title="Orders or POs delivered on or after its date are still on another cost, and nobody has answered yet — use Review orders"
                        >
                          ORDERS TO REVIEW
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="actions-col">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                      <button
                        className="btn-icon"
                        onClick={() => handleEdit(row)}
                        title="Edit"
                      >
                        <span className="material-symbols-rounded">edit</span>
                      </button>
                      <button
                        className="btn-icon"
                        onClick={() => setHistoryRow(row)}
                        title="View change history"
                      >
                        <span className="material-symbols-rounded">history</span>
                      </button>
                      <button
                        className="btn-icon"
                        onClick={() => handleSync(row)}
                        disabled={row.is_synced}
                        title={row.is_synced ? 'Already synced' : 'Sync to Xero'}
                      >
                        <span className="material-symbols-rounded">cloud_upload</span>
                      </button>
                      {(() => {
                        const block = !seeCosts ? 'Needs permission to see costs' : reviewBlock(row);
                        return (
                          <button
                            className={`btn-icon${block ? ' btn-disabled' : ''}`}
                            onClick={() => setReprice({ changeIds: [row.id], intro: `${row.item_code}: orders and POs delivered on or after its effective date.` })}
                            disabled={!!block}
                            title={block || 'Review orders — the orders and POs this cost change leaves on another cost'}
                          >
                            <span className="material-symbols-rounded">price_change</span>
                          </button>
                        );
                      })()}
                      <button
                        className="btn-icon btn-danger"
                        onClick={() => handleDelete(row)}
                        title="Delete"
                      >
                        <span className="material-symbols-rounded">delete</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <AlertDialog
        isOpen={alertDialog.isOpen}
        message={alertDialog.message}
        type={alertDialog.type}
        onClose={() => setAlertDialog(prev => ({ ...prev, isOpen: false }))}
      />
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        confirmText={confirmDialog.confirmText || 'Confirm'}
        cancelText={confirmDialog.cancelText || 'Cancel'}
        onCancel={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
      />
      <AuditHistoryModal
        isOpen={!!historyRow}
        onClose={() => setHistoryRow(null)}
        entityType="price_change"
        entityId={historyRow?.id}
        entityIdentifier={historyRow ? `${historyRow.item_code} — ${historyRow.item_name}` : null}
      />

      {importOpen && (
        <PriceChangeImportModal
          items={items}
          scheduling={scheduling}
          today={today}
          onClose={() => { setImportOpen(false); loadRows(); }}
          onQueued={({ created, updated, effective_date, auto_post }) => {
            setImportOpen(false);
            loadRows();
            const parts = [];
            if (created) parts.push(`${created} queued`);
            if (updated) parts.push(`${updated} already-queued change${updated === 1 ? '' : 's'} updated`);
            const later = scheduling && postsTomorrow({ is_synced: false, auto_post, effective_date }, { today })
              ? ` ${POSTS_TOMORROW_TEXT} Use Sync to Xero to post them now instead.`
              : '';
            const back = scheduling && effective_date && String(effective_date).slice(0, 10) < today
              ? `\n\n${backdatedNotice([effective_date])}`
              : '';
            setAlertDialog({ isOpen: true, message: `Price list imported: ${parts.join(', ')}.${later}${back}`, type: 'success' });
          }}
        />
      )}

      {/* Asked straight after a post, and from Review orders / Orders to
          review: the orders and POs still on another cost. */}
      <PriceSyncUpdatesModal
        isOpen={!!reprice}
        scope={reprice ? { changeIds: reprice.changeIds } : null}
        intro={reprice?.intro || null}
        onClose={() => { setReprice(null); loadRows(); loadPendingReview(); }}
      />
    </div>
  );
}

export default InventoryPriceChanges;
